import { Accordion, Badge, WContainer, WImage } from '@components/atoms'
import { FormatsThickness } from '@components/icons'
import { useLocale, useTrans } from '@hooks/index'
import { TonalitaItemType } from '@utils/types'
import styles from './SuperficiTonalita.module.sass'
import { TonalitaDetailObject } from '@gql/graphql'

interface Props {
  tonalita: TonalitaDetailObject
  className?: string
}

const SuperficiTonalita = (props: Props) => {
  const { tonalita, className = '' } = props
  const t = useTrans()
  const { isAmerica } = useLocale()

  return tonalita.tonalitaCollezione?.superficiSpessoriFormati?.length ? (
    <div className={`${styles.root} ${className}`}>
      <WContainer className={styles.container}>
        <div className={styles.firstContainer}>
          {!!tonalita.brand && (
            <h2 className={styles.title}>{t('Superfici, spessori e formati disponibili')}</h2>
          )}
        </div>

        <div className={styles.desktop}>
          <div className={styles.tableHeader}>
            <h3 className={styles.tableHeaderItem}>{t('Superfici')}</h3>
            <h3 className={styles.tableHeaderItem}>{t('Spessori')}</h3>
            <h3 className={styles.tableHeaderItem}>{t('Formati')}</h3>
          </div>

          <div className={styles.tableContainer}>
            {tonalita.tonalitaCollezione.superficiSpessoriFormati.map((superficie, index) => (
              <div className={styles.tableRow} key={index}>
                <div className={styles.tableRowItemTitle}>
                  <h4 className={styles.superficieTitle}>{superficie.nome}</h4>
                  <div className={styles.tableRowItemTitleInfo}>
                    {superficie?.scivolositaNome && superficie?.scivolositaLabel && (
                      <p className={styles.superficieInfoItemKey}>
                        {superficie.scivolositaNome}{' '}
                        <span className={styles.superficieInfoItemValue}>
                          {superficie.scivolositaLabel}
                        </span>
                      </p>
                    )}

                    {superficie?.specificheAbcNome && superficie?.specificheAbcLabel && (
                      <p className={styles.superficieInfoItemKey}>
                        {superficie.specificheAbcNome}{' '}
                        <span className={styles.superficieInfoItemValue}>
                          {superficie.specificheAbcLabel}
                        </span>
                      </p>
                    )}

                    {superficie?.dcof && (
                      <p className={styles.superficieInfoItemKey}>
                        {superficie.dcof}{' '}
                        <span className={styles.superficieInfoItemValue}>{t('DCOF')}</span>
                      </p>
                    )}

                    {superficie?.bcra && (
                      <p className={styles.superficieInfoItemKey}>
                        {superficie.bcra}{' '}
                        <span className={styles.superficieInfoItemValue}>{t('B.C.R.A')}</span>
                      </p>
                    )}

                    {superficie?.pendoloAS4586 && (
                      <p className={styles.superficieInfoItemKey}>
                        {superficie.pendoloAS4586}{' '}
                        <span className={styles.superficieInfoItemValue}>{t('AS 4586')}</span>
                      </p>
                    )}

                    {superficie?.pendoloBS7976 && (
                      <p className={styles.superficieInfoItemKey}>
                        {superficie.pendoloBS7976}{' '}
                        <span className={styles.superficieInfoItemValue}>{t('BS 7976')}</span>
                      </p>
                    )}

                    {superficie?.pendoloUNE41901 && (
                      <p className={styles.superficieInfoItemKey}>
                        {superficie.pendoloUNE41901}{' '}
                        <span className={styles.superficieInfoItemValue}>{t('UNE 41901')}</span>
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.tableRowItemInfo}>
                  {superficie.spessori.length > 0 &&
                    superficie.spessori.map((superficieItem, index1) => (
                      <div className={styles.tableRowItemSpessori} key={index1}>
                        <h4 className={styles.superficieTitle}>
                          <FormatsThickness className={styles.tableRowItemSpessoriIcon} />
                          {superficieItem.spessore}
                        </h4>
                        <div className={styles.tableRowItemFormati}>
                          {superficieItem.formati.length > 0 && (
                            <div className={styles.tableRowItemFormatiContent}>
                              {superficieItem.formati.map((formato, index2) => {
                                if (formato && formato?.lato1Intero && isAmerica)
                                  return (
                                    <Badge
                                      content={`${formato?.lato1Intero}<sup>${formato?.lato1Numeratore}/${formato?.lato1Denominatore}"</sup>x ${formato?.lato2Intero}<sup>${formato?.lato2Numeratore}/${formato?.lato2Denominatore}"</sup>`}
                                      key={'f' + index2}
                                    />
                                  )
                                else return <Badge label={formato.nome} key={'f' + index2} />
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.mobile}>
          {tonalita.tonalitaCollezione.superficiSpessoriFormati.map((superficie, index) => (
            <Accordion
              variant="box"
              id={superficie.nome}
              className={styles.mobileAccordion}
              label={superficie.nome}
              labelClassName={styles.mobileAccordionLabel}
              key={index}
            >
              <div>
                <div
                  className={`${styles.tableRowItemTitleInfo} ${styles.mobileAccordionItemWrapper}`}
                >
                  {superficie?.scivolositaNome && superficie?.scivolositaLabel && (
                    <p className={styles.superficieInfoItemKey}>
                      {superficie.scivolositaNome}{' '}
                      <span className={styles.superficieInfoItemValue}>
                        {superficie.scivolositaLabel}
                      </span>
                    </p>
                  )}

                  {superficie?.specificheAbcNome && superficie?.specificheAbcLabel && (
                    <p className={styles.superficieInfoItemKey}>
                      {superficie.specificheAbcNome}{' '}
                      <span className={styles.superficieInfoItemValue}>
                        {superficie.specificheAbcLabel}
                      </span>
                    </p>
                  )}

                  {superficie?.dcof && (
                    <p className={styles.superficieInfoItemKey}>
                      {superficie.dcof}{' '}
                      <span className={styles.superficieInfoItemValue}>{t('DCOF')}</span>
                    </p>
                  )}

                  {superficie?.bcra && (
                    <p className={styles.superficieInfoItemKey}>
                      {superficie.bcra}{' '}
                      <span className={styles.superficieInfoItemValue}>{t('B.C.R.A')}</span>
                    </p>
                  )}

                  {superficie?.pendoloAS4586 && (
                    <p className={styles.superficieInfoItemKey}>
                      {superficie.pendoloAS4586}{' '}
                      <span className={styles.superficieInfoItemValue}>{t('AS 4586')}</span>
                    </p>
                  )}

                  {superficie?.pendoloBS7976 && (
                    <p className={styles.superficieInfoItemKey}>
                      {superficie.pendoloBS7976}{' '}
                      <span className={styles.superficieInfoItemValue}>{t('BS 7976')}</span>
                    </p>
                  )}

                  {superficie?.pendoloUNE41901 && (
                    <p className={styles.superficieInfoItemKey}>
                      {superficie.pendoloUNE41901}{' '}
                      <span className={styles.superficieInfoItemValue}>{t('UNE 41901')}</span>
                    </p>
                  )}
                </div>

                {superficie.spessori.map((superficieItem, index) => (
                  <div className={styles.mobileAccordionItemWrapper} key={index}>
                    <div className={styles.mobileAccordionContentItem}>
                      <h4 className={styles.superficieTitle}>
                        <FormatsThickness className={styles.tableRowItemSpessoriIcon} />
                        {superficieItem.spessore}
                      </h4>
                    </div>
                    <div className={styles.mobileAccordionContentItemFormati}>
                      {superficieItem.formati.map((formato, index2) => {
                        if (formato && formato?.lato1Intero && isAmerica)
                          return (
                            <Badge
                              content={`${formato?.lato1Intero}<sup>${formato?.lato1Numeratore}/${formato?.lato1Denominatore}"</sup>x ${formato?.lato2Intero}<sup>${formato?.lato2Numeratore}/${formato?.lato2Denominatore}"</sup>`}
                              key={'f' + index2}
                            />
                          )
                        else return <Badge label={formato.nome} key={'f' + index2} />
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Accordion>
          ))}
        </div>

        <div className={styles.thirdContainer}>
          {tonalita.collezione.shadeVariations.length > 0 && (<div className={styles.thirdContainerItem}>
            <h3 className={styles.thirdContainerItemTitle}>{t('Shade variations')}</h3>
            {tonalita.collezione.shadeVariations.map((shadeVariation, index) => (
              <div className={styles.thirdContainerItemContent1} key={index}>
                <WImage
                  className={styles.thirdContainerItemContent1Image}
                  src={shadeVariation.imageUrl}
                  alt={shadeVariation?.imageAlt}
                  maxWidth={85}
                  maxHeight={85}
                />
                <p className={styles.thirdContainerItemContent1Text}>{shadeVariation.nome}</p>
              </div>
            ))}
          </div>)}
           {tonalita.collezione.destinazioni.length > 0 && ( <div className={styles.thirdContainerItem}>
            <h3 className={styles.thirdContainerItemTitle}>{t("Destinazioni d'uso")}</h3>
            <div className={styles.thirdContainerItemContent2}>
              {tonalita.collezione.destinazioni.map((destinazione, index) => (
                <div className={styles.thirdContainerItemContent2Item} key={index}>
                
                  <WImage
                    className={styles.thirdContainerItemContent1Image}
                    alt={destinazione?.imageAlt}
                    src={destinazione.imageUrl}
                    maxWidth={85}
                    maxHeight={85}
                  />
                </div>
              ))}
            </div>
          </div>)}
        </div>
      </WContainer>
    </div>
  ) : (
    <></>
  )
}

export default SuperficiTonalita
